/** Shared hover and tap tooltips with portalled content and scroll-boundary positioning. */
"use client";

import { Portal } from "@ark-ui/react/portal";
import { Tooltip as ArkTooltip } from "@ark-ui/react/tooltip";
import { IconInfo } from "@decibeltrade/react-icons";
import { JSX, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { css, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

import { useIsTouchDevice } from "@/hooks/use-is-touch-device";
import { Placement } from "@/types/ui";

const TOUCH_DISMISS_DELAY = 3000;

export interface TooltipProps {
  /** The element that should trigger the tooltip on hover */
  children: JSX.Element;
  /** The content to be rendered inside the tooltip */
  content: ReactNode;
  /** Styles the padded content wrapper on both desktop and touch. */
  contentClassName?: string;
  /** If `true` the tooltip will not display */
  disabled?: boolean;
  /** Whether or not an arrow should be displayed on the inner edge of the tooltip */
  showArrow?: boolean;
  /** Where the tooltip should be placed relative to the trigger element */
  placement?: Placement;
  /** A delay in milliseconds before the tooltip should be displayed */
  openDelay?: number;
  /** A delay in milliseconds before the tooltip is dismissed */
  closeDelay?: number;
  /** Whether or not the tooltip should be dismissed on pointer down */
  closeOnPointerDown?: boolean;
  /** Whether or not the tooltip should be unmounted when not visible */
  mountOnlyWhenVisible?: boolean;
  /** Whether or not the tooltip will remain visible when the tooltip's content is hovered */
  interactive?: boolean;
  /** A custom label for the tooltip */
  ariaLabel?: string;
  /** An anchor element to position the tooltip relative to */
  anchorElement?: HTMLDivElement | HTMLButtonElement | null;
  /** Controls visibility directly. Omit to keep the default hover behaviour. */
  open?: boolean;
  /** Reports requested visibility changes from hover, taps, or dismissal. */
  onOpenChange?: (open: boolean) => void;
  /** Content holds a control: stay pointer-interactive on touch and never auto-dismiss. */
  touchInteractive?: boolean;
  /** Clips the tooltip to a scroll container rather than to the viewport. */
  boundary?: () => Element | "clippingAncestors";
  /** Hides the tooltip, without unmounting it, once its anchor is clipped out of `boundary`. */
  hideWhenDetached?: boolean;
}

/** A component that will cause a tooltip to be displayed when the child element is hovered. */
export function Tooltip(props: TooltipProps) {
  const isTouchDevice = useIsTouchDevice();

  // false (SSR/initial hydration/desktop) renders the uncontrolled hover tooltip.
  // true (touch device, after hydration) renders the controlled touch tooltip.
  if (isTouchDevice) {
    return <TouchTooltip {...props} />;
  }

  return <HoverTooltip {...props} />;
}

function HoverTooltip({
  children,
  content,
  contentClassName,
  disabled,
  showArrow = true,
  placement = "top",
  openDelay = 250,
  closeDelay = 250,
  closeOnPointerDown = true,
  mountOnlyWhenVisible = false,
  interactive = true,
  ariaLabel,
  anchorElement,
  open,
  onOpenChange,
  boundary,
  hideWhenDetached,
}: TooltipProps) {
  return (
    <ArkTooltip.Root
      positioning={{
        gutter: 6,
        placement,
        getAnchorRect: () => anchorElement?.getBoundingClientRect() ?? null,
        boundary,
        hideWhenDetached,
      }}
      open={open}
      onOpenChange={onOpenChange && ((details) => onOpenChange(details.open))}
      disabled={disabled}
      openDelay={openDelay}
      closeDelay={closeDelay}
      closeOnPointerDown={closeOnPointerDown}
      lazyMount={mountOnlyWhenVisible}
      unmountOnExit={mountOnlyWhenVisible}
      interactive={interactive}
      aria-label={ariaLabel}
    >
      <ArkTooltip.Trigger asChild>{children}</ArkTooltip.Trigger>
      <Portal>
        <ArkTooltip.Positioner className={interactive ? undefined : nonInteractivePositionerStyles}>
          <ArkTooltip.Content className={tooltipContentAnimationStyles}>
            {showArrow && <TooltipArrow />}
            <div className={cx(contentStyles, contentClassName)}>{content}</div>
          </ArkTooltip.Content>
        </ArkTooltip.Positioner>
      </Portal>
    </ArkTooltip.Root>
  );
}

function TouchTooltip({
  children,
  content,
  contentClassName,
  disabled,
  showArrow = true,
  placement = "top",
  mountOnlyWhenVisible = false,
  ariaLabel,
  anchorElement,
  open: controlledOpen,
  onOpenChange,
  touchInteractive = false,
  boundary,
  hideWhenDetached,
}: TooltipProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [controlledOpen, onOpenChange],
  );

  // Preserve the trigger's native click behavior.
  const handleClick = useCallback(() => {
    if (disabled) return;
    setOpen(!open);
  }, [disabled, open, setOpen]);

  // Auto-dismiss after 3 seconds, unless the content holds a control the user is reaching for.
  useEffect(() => {
    if (!open || touchInteractive) return;

    const timer = setTimeout(() => setOpen(false), TOUCH_DISMISS_DELAY);
    return () => clearTimeout(timer);
  }, [open, touchInteractive, setOpen]);

  // Keep interactive controls mounted until their touch becomes a click.
  useEffect(() => {
    if (!open) return;

    const handleOutsideTap = (event: TouchEvent) => {
      const target = event.target;
      if (target instanceof Node && triggerRef.current?.contains(target)) return;
      if (
        touchInteractive &&
        target instanceof Element &&
        target.closest("[data-scope='tooltip'][data-part='content']")
      ) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("touchstart", handleOutsideTap);
    return () => document.removeEventListener("touchstart", handleOutsideTap);
  }, [open, touchInteractive, setOpen]);

  return (
    <ArkTooltip.Root
      positioning={{
        gutter: 6,
        placement,
        getAnchorRect: () => anchorElement?.getBoundingClientRect() ?? null,
        boundary,
        hideWhenDetached,
      }}
      disabled={disabled}
      open={open}
      onOpenChange={(details) => setOpen(details.open)}
      closeOnPointerDown={false}
      closeOnClick={false}
      lazyMount={mountOnlyWhenVisible}
      unmountOnExit={mountOnlyWhenVisible}
      interactive={touchInteractive}
      aria-label={ariaLabel}
    >
      <ArkTooltip.Trigger ref={triggerRef} asChild onClick={handleClick}>
        {children}
      </ArkTooltip.Trigger>
      <Portal>
        <ArkTooltip.Positioner>
          <ArkTooltip.Content className={tooltipContentAnimationStyles}>
            {showArrow && <TooltipArrow />}
            <div className={cx(contentStyles, contentClassName)}>{content}</div>
          </ArkTooltip.Content>
        </ArkTooltip.Positioner>
      </Portal>
    </ArkTooltip.Root>
  );
}

function TooltipArrow() {
  return (
    <ArkTooltip.Arrow className={arrowStyles}>
      <ArkTooltip.ArrowTip />
    </ArkTooltip.Arrow>
  );
}

export type TooltipTextProps = {
  children?: ReactNode;
  className?: string;
  /** On touch devices with "icon" mode, an info icon is rendered as the tooltip trigger
   * instead of the text itself. */
  touchMode?: "tap" | "icon";
} & Omit<TooltipProps, "children">;

export const TextTooltip = ({
  children,
  className,
  content,
  ...rest
}: {
  children?: ReactNode;
  className?: string;
} & Omit<TooltipProps, "children">) => (
  <Tooltip content={content} {...rest}>
    <span
      className={cx(
        css({
          textDecoration: "underline",
          cursor: "help",
          textUnderlineOffset: "4",
        }),
        className,
      )}
    >
      {children}
    </span>
  </Tooltip>
);

export const TooltipText = ({
  children,
  className,
  touchMode = "tap",
  ...rest
}: TooltipTextProps) => {
  const isTouchDevice = useIsTouchDevice();

  if (isTouchDevice && touchMode === "icon") {
    return (
      <span className={cx(inlineWithIconStyles, className)}>
        {children}
        <Tooltip {...rest}>
          <span
            className={infoIconTriggerStyles}
            role="button"
            aria-label="More information"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <IconInfo className={infoIconStyles} />
          </span>
        </Tooltip>
      </span>
    );
  }

  return (
    <Tooltip {...rest}>
      <span className={cx(textTriggerStyles, className)}>{children}</span>
    </Tooltip>
  );
};

const nonInteractivePositionerStyles = css({ pointerEvents: "none" });

const tooltipContentAnimationStyles = css({
  zIndex: "popover",
  _open: { animationStyle: "fade-in" },
  _closed: { animationStyle: "fade-out" },
});

const arrowStyles = css({
  "--arrow-size": "{spacing.8}",
  "--arrow-background": "{colors.tooltip.background}",
});

const textTriggerStyles = css({
  textDecoration: "underline",
  cursor: "help",
  textUnderlineOffset: "4",
  textDecorationStyle: "dashed",
});

const inlineWithIconStyles = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "4",
});

const infoIconTriggerStyles = css({
  display: "inline-flex",
  alignItems: "center",
  color: "text.tertiary",
  cursor: "help",
});

const infoIconStyles = css({
  h: "12",
  w: "12",
});

const contentStyles = flex({
  align: "center",
  justify: "center",
  textAlign: "center",
  maxW: "[200px]",
  px: "8",
  py: "8",
  gap: "8",
  textStyle: "body.sm",
  color: "tooltip.text",
  bg: "tooltip.background",
  rounded: "primary",
  whiteSpace: "wrap",
});
